import React, { useState } from 'react';
import { Lock, AlertCircle, Loader2, Eye, EyeOff, CheckCircle, ShieldCheck } from 'lucide-react';
import { api } from '../../api/client';
import {
    generateSalt,
    deriveKeyFromPassword,
    encryptPrivateKey,
    encodeBase64,
    generateEscrowKey,
    STORAGE_KEY
} from '../../utils/crypto';
import OAuthRecoveryStep from './OAuthRecoveryStep';
import nacl from 'tweetnacl';
import './Auth.css';

interface SetupPasswordPageProps {
    email: string;
    inviteToken?: string;
    onSuccess: () => void;
    onBack: () => void;
}

type Step = 'password' | 'oauth';

export default function SetupPasswordPage({ email, inviteToken, onSuccess, onBack }: SetupPasswordPageProps) {
    const [step, setStep] = useState<Step>('password');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [oauthConfigured, setOauthConfigured] = useState(false);
    // Store the raw private key between steps so OAuthRecoveryStep can encrypt it
    const [rawPrivateKey, setRawPrivateKey] = useState<string>('');

    // Prefetch OAuth config so we can adapt button text and hide the step-2 indicator
    React.useEffect(() => {
        api.getOAuthConfig()
            .then((cfg) => setOauthConfigured(!!(cfg.google_client_id || cfg.microsoft_client_id)))
            .catch(() => setOauthConfigured(false));
    }, []);

    const validatePassword = (pwd: string) => {
        if (pwd.length < 8) return 'Password must be at least 8 characters';
        if (!/[A-Z]/.test(pwd)) return 'Password must contain an uppercase letter';
        if (!/[a-z]/.test(pwd)) return 'Password must contain a lowercase letter';
        if (!/[0-9]/.test(pwd)) return 'Password must contain a number';
        return null;
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const validationError = validatePassword(password);
        if (validationError) { setError(validationError); return; }
        if (password !== confirmPassword) { setError('Passwords do not match'); return; }

        setIsLoading(true);
        try {
            // 1. Generate E2EE keypair
            const keyPair = nacl.box.keyPair();
            const publicKey = encodeBase64(keyPair.publicKey);
            const privateKey = encodeBase64(keyPair.secretKey);

            // 2. Derive wrapping key from password and encrypt the private key
            const salt = generateSalt();
            const wrappingKey = await deriveKeyFromPassword(password, salt);
            const encPrivateKey = await encryptPrivateKey(privateKey, wrappingKey);

            // 3. Setup password + upload keys
            const response = await api.setupPassword(email, inviteToken, password, {
                public_key: publicKey,
                enc_private_key: encPrivateKey,
                key_salt: salt,
            });

            // 4. Store keys in localStorage for this session (user-specific)
            const userId = response.user?.id;
            const storageKey = userId ? `${STORAGE_KEY}_${userId}` : STORAGE_KEY;
            localStorage.setItem(storageKey, JSON.stringify({ publicKey, privateKey }));

            // 5. Generate escrow blob and send to server (server emails admin)
            const { recoveryKey, encPrivateKeyEscrow } = await generateEscrowKey(privateKey);
            await api.saveKeys({
                enc_private_key: encPrivateKey,
                key_salt: salt,
                public_key: publicKey,
                enc_private_key_escrow: encPrivateKeyEscrow,
                recovery_key: recoveryKey,
            });

            // 6. Store private key and move to OAuth step
            setRawPrivateKey(privateKey);
            setStep('oauth');
        } catch (err: any) {
            localStorage.removeItem('auth_token');
            setError(
                err?.response?.data?.error ||
                err?.message ||
                'Account setup failed. Please ask your administrator to configure email settings and try again.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    const requirements = [
        { met: password.length >= 8, text: 'At least 8 characters' },
        { met: /[A-Z]/.test(password), text: 'One uppercase letter' },
        { met: /[a-z]/.test(password), text: 'One lowercase letter' },
        { met: /[0-9]/.test(password), text: 'One number' },
    ];

    return (
        <div className="auth-container">
            <div className="auth-card">
                {step === 'password' ? (
                    <>
                        <div className="auth-header">
                            <div className="auth-logo">TC</div>
                            <h1>Set Up Your Password</h1>
                            <p>Create a secure password for {email}</p>
                        </div>

                        {/* Step indicator: only show multi-step UI when OAuth is available */}
                        {oauthConfigured && (
                            <div className="auth-steps">
                                <div className="auth-step active">
                                    <span className="auth-step-num">1</span>
                                    <span>Set Password</span>
                                </div>
                                <div className="auth-step-divider" />
                                <div className="auth-step">
                                    <span className="auth-step-num">2</span>
                                    <span>Recovery Account</span>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handlePasswordSubmit} className="auth-form">
                            {/* E2EE warning */}
                            <div className="auth-info">
                                <span className="auth-info-icon">🔐</span>
                                <div className="auth-info-content">
                                    <span className="auth-info-title">Your password protects your encryption key</span>
                                    <p className="auth-info-text">
                                        Messages are end-to-end encrypted. <strong>Remember your password</strong> — if you forget it without a recovery account, you lose access to your encrypted message history.
                                    </p>
                                </div>
                            </div>

                            {error && (
                                <div className="auth-error">
                                    <AlertCircle size={16} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <div className="auth-field">
                                <label htmlFor="password">New Password</label>
                                <div className="auth-input-wrapper">
                                    <Lock size={18} />
                                    <input
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        className="auth-toggle-password"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <div className="password-requirements">
                                {requirements.map((req, i) => (
                                    <div key={i} className={`requirement ${req.met ? 'met' : ''}`}>
                                        <CheckCircle size={14} />
                                        <span>{req.text}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="auth-field">
                                <label htmlFor="confirm">Confirm Password</label>
                                <div className="auth-input-wrapper">
                                    <Lock size={18} />
                                    <input
                                        id="confirm"
                                        type={showPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="••••••••"
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>

                            <button type="submit" className="auth-submit" disabled={isLoading}>
                                {isLoading ? (
                                    <><Loader2 size={18} className="spin" />Setting up...</>
                                ) : oauthConfigured ? (
                                    'Next: Link Recovery Account →'
                                ) : (
                                    'Complete Setup'
                                )}
                            </button>

                            <button type="button" className="auth-link" onClick={onBack}>
                                Back to login
                            </button>
                        </form>
                    </>
                ) : (
                    <>
                        <div className="auth-header">
                            <div className="auth-logo" style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
                                <ShieldCheck size={28} />
                            </div>
                            <h1>Link a Recovery Account</h1>
                            <p>This lets you recover your encryption key if you forget your password.</p>
                        </div>

                        {/* Step indicator */}
                        <div className="auth-steps">
                            <div className="auth-step done">
                                <span className="auth-step-num">✓</span>
                                <span>Password Set</span>
                            </div>
                            <div className="auth-step-divider" />
                            <div className="auth-step active">
                                <span className="auth-step-num">2</span>
                                <span>Recovery Account</span>
                            </div>
                        </div>

                        <div style={{ padding: '0 0 16px' }}>
                            <OAuthRecoveryStep
                                privateKey={rawPrivateKey}
                                onSuccess={onSuccess}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
